import { Sparkles, ArrowRight, ArrowLeft, User, Trophy } from "lucide-react";
import { cn } from "@/lib/utils";
import { muteVideoRef } from "@/lib/muteVideoRef";
import { useLanguage } from "@/contexts/LanguageContext";
import { BaseballTrendUpload } from "./BaseballTrendUpload";
import {
  BASEBALL_TREND_EXAMPLE_AFTER,
  BASEBALL_TREND_EXAMPLE_BEFORE,
} from "@/config/baseballTrend";
import { BASEBALL_TREND_PRESETS_ANCHOR_ID } from "@/pages/BaseballTrend/constants";
import type { UploadedSource } from "@/pages/BaseballTrend/types";

interface BaseballTrendHeroProps {
  upload: UploadedSource | null;
  isUploading: boolean;
  canGenerate: boolean;
  onAddFile: (file: File) => void;
  onRemove: () => void;
  onGenerate: () => void;
}

export function BaseballTrendHero({
  upload,
  isUploading,
  canGenerate,
  onAddFile,
  onRemove,
  onGenerate,
}: BaseballTrendHeroProps) {
  const { t, isRTL } = useLanguage();
  const hasUpload = !!upload;

  // Hero "before/after" pair:
  //  - "before" → user's uploaded photo, else the configured example image
  //  - "after"  → after the user uploads we show a "Create now" CTA over the
  //               placeholder, building anticipation for the broadcast clip.
  const beforeImage = upload?.previewUrl || BASEBALL_TREND_EXAMPLE_BEFORE || undefined;
  const afterImage = !hasUpload ? BASEBALL_TREND_EXAMPLE_AFTER || undefined : undefined;
  const afterShowsPlaceholder = hasUpload;

  return (
    <div className="rounded-3xl border border-border bg-gradient-to-b from-primary/[0.05] to-transparent p-5 md:p-8">
      <div className="text-center mb-6 md:mb-8">
        <h1 className="text-3xl md:text-5xl font-bold text-foreground mb-2 md:mb-3 bg-gradient-to-r from-primary to-foreground bg-clip-text text-transparent">
          {t.baseballTrend.heroTitle}
        </h1>
        <p className="text-sm md:text-base text-muted-foreground max-w-xl mx-auto">
          {t.baseballTrend.heroSubtitle}
        </p>
      </div>

      {hasUpload ? (
        // After upload: showcase prominently on top, upload thumb below.
        <div className="space-y-4 md:space-y-5 animate-in fade-in slide-in-from-bottom-4 duration-500">
          <div className="max-w-xl mx-auto w-full">
            <BeforeAfterShowcase
              beforeImage={beforeImage}
              afterImage={afterImage}
              afterShowsPlaceholder={afterShowsPlaceholder}
              canGenerate={canGenerate}
              onGenerate={onGenerate}
              isRTL={isRTL}
            />
          </div>
          <div className="max-w-sm mx-auto w-full">
            <BaseballTrendUpload
              upload={upload}
              isUploading={isUploading}
              onAddFile={onAddFile}
              onRemove={onRemove}
              variant="compact"
            />
          </div>
        </div>
      ) : (
        // Initial: showcase + big drop-zone side-by-side.
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-8 items-start">
          <BeforeAfterShowcase
            beforeImage={beforeImage}
            afterImage={afterImage}
            afterShowsPlaceholder={false}
            canGenerate={false}
            onGenerate={onGenerate}
            isRTL={isRTL}
          />
          <BaseballTrendUpload
            upload={null}
            isUploading={isUploading}
            onAddFile={onAddFile}
            onRemove={onRemove}
            variant="hero"
          />
        </div>
      )}
    </div>
  );
}

function BeforeAfterShowcase({
  beforeImage,
  afterImage,
  afterShowsPlaceholder,
  canGenerate,
  onGenerate,
  isRTL,
}: {
  beforeImage: string | undefined;
  afterImage: string | undefined;
  afterShowsPlaceholder: boolean;
  canGenerate: boolean;
  onGenerate: () => void;
  isRTL: boolean;
}) {
  const { t } = useLanguage();
  const Arrow = isRTL ? ArrowLeft : ArrowRight;

  return (
    <div className="grid grid-cols-2 gap-3 md:gap-4 relative">
      <ShowcaseCard
        label={t.baseballTrend.heroBeforeLabel}
        kind="before"
        image={beforeImage}
      />
      <ShowcaseCard
        label={t.baseballTrend.heroAfterLabel}
        kind="after"
        image={afterImage}
        showCta={afterShowsPlaceholder}
        canGenerate={canGenerate}
        onGenerate={onGenerate}
        ctaLabel={t.baseballTrend.generateBtn}
      />
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-background border border-border shadow-md flex items-center justify-center pointer-events-none">
        <Arrow className="w-4 h-4 text-primary" />
      </div>
    </div>
  );
}

function ShowcaseCard({
  label,
  kind,
  image,
  showCta,
  canGenerate,
  onGenerate,
  ctaLabel,
}: {
  label: string;
  kind: "before" | "after";
  image?: string;
  showCta?: boolean;
  canGenerate?: boolean;
  onGenerate?: () => void;
  ctaLabel?: string;
}) {
  const Icon = kind === "before" ? User : Trophy;
  // Treat any .mp4 (or .webm/.mov) URL as a video and render <video> so the
  // hero can showcase the actual Kling-generated clip on the "after" tile.
  const isVideo = !!image && /\.(mp4|webm|mov)(\?|$)/i.test(image);
  return (
    <div className="relative aspect-[4/5] rounded-2xl overflow-hidden border border-border bg-gradient-to-br from-muted to-secondary">
      {image ? (
        isVideo ? (
          <video
            ref={muteVideoRef}
            src={image}
            autoPlay
            loop
            muted
            playsInline
            preload="metadata"
            className="absolute inset-0 w-full h-full object-cover"
            aria-label={label}
          />
        ) : (
          <img
            src={image}
            alt={label}
            className="absolute inset-0 w-full h-full object-cover"
          />
        )
      ) : showCta && onGenerate && ctaLabel ? (
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 px-3 text-center">
          <button
            type="button"
            onClick={() => {
              if (canGenerate) {
                onGenerate();
                return;
              }
              const target = document.getElementById(BASEBALL_TREND_PRESETS_ANCHOR_ID);
              target?.scrollIntoView({ behavior: "smooth", block: "start" });
            }}
            className={cn(
              "inline-flex items-center justify-center gap-2 px-4 md:px-5 h-11 md:h-12 rounded-xl bg-gradient-to-r gradient-primary text-white text-sm md:text-base font-bold whitespace-nowrap transition-all",
              "shadow-[0_0_20px_hsl(var(--brand)/0.35),0_4px_16px_rgba(0,0,0,0.2)] hover:shadow-[0_0_28px_hsl(var(--brand)/0.55)] hover:scale-[1.04] active:scale-[0.97]",
            )}
          >
            <Sparkles className="w-4 h-4 md:w-5 md:h-5" />
            <span>{ctaLabel}</span>
          </button>
        </div>
      ) : (
        <div className="absolute inset-0 flex items-center justify-center">
          <Icon className="w-10 h-10 text-primary/30" />
        </div>
      )}
      <div className="absolute top-2 md:top-2.5 start-2 md:start-2.5">
        <span className="inline-flex items-center px-2 py-0.5 md:px-2.5 md:py-1 rounded-md bg-black/75 backdrop-blur-sm text-[10px] md:text-xs font-bold text-white uppercase tracking-wider shadow-sm ring-1 ring-white/10">
          {label}
        </span>
      </div>
    </div>
  );
}
